import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiCommonService } from '@api-services/api-common.service';
import { ApiAlertError } from '@decorators/api-alert-error';
import { HttpHelper } from '@helpers/http.helper';
import { TaskFiltersHelper } from '@helpers/task-filters.helper';
import { TaskFilters } from '@interfaces/task-filters';
import { TimeTrackingsCategory, TimeTrackingsCategoryJson } from '@models/time-trackings-category';
import { TimeTrackingsDay, TimeTrackingsDayJson } from '@models/time-trackings-day';
import { TimeTrackingsDurationType, TimeTrackingsDurationTypeJson } from '@models/time-trackings-duration-type';
import { TimeTrackingsInvoicingType, TimeTrackingsInvoicingTypeJson } from '@models/time-trackings-invoicing-type';
import { TimeTrackingsRecord, TimeTrackingsRecordJson } from '@models/time-trackings-record';
import { TimeTrackingsTask, TimeTrackingsTaskJson } from '@models/time-trackings-task';

/**
 * Time Trackings Api Service.
 */
@Injectable({
  providedIn: 'root'
})
export class TimeTrackingsApiService extends ApiCommonService<TimeTrackingsRecord> {
  resourceUrl = 'api/v1/expert/time_tracking';

  fromJson(json: TimeTrackingsRecordJson): TimeTrackingsRecord {
    return TimeTrackingsRecord.fromJson(json);
  }

  toJson(model: TimeTrackingsRecord): Partial<TimeTrackingsRecordJson> {
    return TimeTrackingsRecord.toJson(model);
  }

  @ApiAlertError()
  getRecords(userId: number, date: string): Observable<TimeTrackingsRecord[]> {
    const url = `${this.resourceUrl}/records`;
    let params = new HttpParams();

    params = HttpHelper.setParam('user_id', userId.toString(), params);
    params = HttpHelper.setParam('date', date, params);

    return this.getAll({ params }, url);
  }

  @ApiAlertError()
  deleteRecord(recordId: number): Observable<unknown> {
    const url = `${this.resourceUrl}/records/${recordId}`;

    return this.http.delete(url);
  }

  @ApiAlertError()
  createRecord(date: string, timeTrackingInvoicingTypeId: number): Observable<TimeTrackingsRecord> {
    const url = `${this.resourceUrl}/records`;
    const body = {
      date,
      time_tracking_invoicing_type: {
        id: timeTrackingInvoicingTypeId
      }
    };

    return this.http.put(url, body).pipe(map((recordJson: TimeTrackingsRecordJson) => this.fromJson(recordJson)));
  }

  @ApiAlertError()
  putRecord(record: TimeTrackingsRecord): Observable<TimeTrackingsRecord> {
    const url = `${this.resourceUrl}/records`;

    return this.http
      .put(url, this.toJson(record))
      .pipe(map((recordJson: TimeTrackingsRecordJson) => this.fromJson(recordJson)));
  }

  @ApiAlertError()
  getCategories(): Observable<TimeTrackingsCategory[]> {
    const url = `${this.resourceUrl}/categories`;

    return this.http
      .get(url)
      .pipe(
        map((jsonArray: TimeTrackingsCategoryJson[]) =>
          jsonArray.map((json: TimeTrackingsCategoryJson) => TimeTrackingsCategory.fromJson(json))
        )
      );
  }

  @ApiAlertError()
  getTasks(filters?: TaskFilters): Observable<TimeTrackingsTask[]> {
    const url = `${this.resourceUrl}/tasks`;
    let params: HttpParams = new HttpParams();

    if (filters) {
      params = HttpHelper.setParams(TaskFiltersHelper.toParams(filters), params);
    }

    return this.http
      .get(url, { params })
      .pipe(
        map((jsonArray: TimeTrackingsTaskJson[]) =>
          jsonArray.map((json: TimeTrackingsTaskJson) => TimeTrackingsTask.fromJson(json))
        )
      );
  }

  @ApiAlertError()
  getInvoicingTypes(): Observable<TimeTrackingsInvoicingType[]> {
    const url = `${this.resourceUrl}/invoicing_types`;

    return this.http
      .get(url)
      .pipe(
        map((jsonArray: TimeTrackingsInvoicingTypeJson[]) =>
          jsonArray.map((json: TimeTrackingsInvoicingTypeJson) => TimeTrackingsInvoicingType.fromJson(json))
        )
      );
  }

  @ApiAlertError()
  getDurationType(): Observable<TimeTrackingsDurationType[]> {
    const url = `${this.resourceUrl}/duration_types`;

    return this.http
      .get(url)
      .pipe(
        map((jsonArray: TimeTrackingsDurationTypeJson[]) =>
          jsonArray.map((json: TimeTrackingsDurationTypeJson) => TimeTrackingsDurationType.fromJson(json))
        )
      );
  }

  @ApiAlertError()
  validateDay(userId: number, date: string, validated: boolean): Observable<void> {
    const url = `${this.resourceUrl}/records/validate`;
    const body = {
      start_date: date,
      end_date: date,
      user_ids: [userId],
      validated
    };

    return this.http.post<void>(url, body);
  }

  @ApiAlertError()
  getDays(userId: number, startDate: string, endDate: string): Observable<TimeTrackingsDay[]> {
    const url = `${this.resourceUrl}/records/days`;
    let params = new HttpParams();

    params = HttpHelper.setParam('owner_ids', String(userId), params);
    params = HttpHelper.setParam('start_date', startDate, params);
    params = HttpHelper.setParam('end_date', endDate, params);

    return this.http
      .get(url, { params })
      .pipe(
        map((timeTrackingsValidatedDaysJson: TimeTrackingsDayJson[]) =>
          timeTrackingsValidatedDaysJson.map((timeTrackingsValidatedDayJson: TimeTrackingsDayJson) =>
            TimeTrackingsDay.fromJson(timeTrackingsValidatedDayJson)
          )
        )
      );
  }
}
